// The timeline's visible window — four quarters from the window start, or the quarters picked
// in the Quarter filter — and which initiatives fall inside it. The timeline
// only draws those, so the page header and the export use this to say
// "6 of 10" and to export exactly what's on screen.

import type { Initiative } from "./types";
import { addMonths, fmtMonthYear, normalizeQuarterId, QUARTERS, quarterBounds, quarterLabel, rollingWindow, WINDOW_MONTHS, type TimeWindow } from "./constants";
import { inWindow } from "./layout";

// Picked quarters, known ones only, in calendar order.
function picked(quarters: string[]): string[] {
  const ids = new Set(quarters.map(normalizeQuarterId));
  return QUARTERS.filter(q => ids.has(q.id)).map(q => q.id);
}

// The Quarter filter picks a run of adjacent quarters. Clicking:
//   - with nothing picked, picks that quarter;
//   - outside the run, stretches the run to reach it;
//   - an end of the run, drops that end (the last one clears);
//   - inside the run, starts over from that quarter.
export function toggleQuarterRange(current: string[], id: string): string[] {
  const all = QUARTERS.map(q => q.id);
  const qid = normalizeQuarterId(id);
  const cur = picked(current);
  const at = all.indexOf(qid);
  if (at < 0) return cur;
  if (!cur.length) return [qid];
  const lo = all.indexOf(cur[0]);
  const hi = all.indexOf(cur[cur.length - 1]);
  if (at < lo) return all.slice(at, hi + 1);
  if (at > hi) return all.slice(lo, at + 1);
  if (lo === hi) return [];
  if (at === lo) return all.slice(lo + 1, hi + 1);
  if (at === hi) return all.slice(lo, hi);
  return [qid];
}

// Earliest picked quarter's start to the latest one's end; null when none.
// Older links may carry quarters with gaps between them: the span covers the gaps.
export function zoomBounds(quarters: string[]): TimeWindow | null {
  const ids = picked(quarters);
  if (!ids.length) return null;
  return { start: quarterBounds(ids[0])!.start, end: quarterBounds(ids[ids.length - 1])!.end };
}

// "FY27 Q1", or "FY26 Q4 – FY27 Q2" for a run.
export function zoomLabel(quarters: string[]): string {
  const ids = picked(quarters);
  if (!ids.length) return "";
  return ids.length === 1 ? quarterLabel(ids[0]) : `${quarterLabel(ids[0])} – ${quarterLabel(ids[ids.length - 1])}`;
}

// Same rule the Timeline uses to build its axis.
export function timelineWindow(windowStart: Date, quarters: string[]): TimeWindow {
  return zoomBounds(quarters) ?? rollingWindow(windowStart);
}

// The rolling window's quarters: "FY26 Q4 – FY27 Q3".
export function rollingLabel(windowStart: Date): string {
  const last = addMonths(windowStart, WINDOW_MONTHS - 1);
  const qOf = (d: Date) => QUARTERS.find(q => d >= q.start && d < q.end);
  const a = qOf(windowStart), b = qOf(last);
  return a && b ? `${a.year} ${a.label} – ${b.year} ${b.label}` : `${fmtMonthYear(windowStart)} – ${fmtMonthYear(last)}`;
}

// "FY26 Q4 – FY27 Q3", or the picked quarters ("FY27 Q1").
export function timelineWindowLabel(windowStart: Date, quarters: string[]): string {
  if (zoomBounds(quarters)) return zoomLabel(quarters);
  return rollingLabel(windowStart);
}

export function inTimelineWindow(items: Initiative[], windowStart: Date, quarters: string[]): Initiative[] {
  const win = timelineWindow(windowStart, quarters);
  return items.filter(i => inWindow(i, win));
}
